import { Button, ButtonGroup } from "../../button";
import { Icon } from "../../icons";
import { useTranslation } from "../../localization";
import type { LightboxAnnotationControls } from "./shared";

/** Props Interface */
interface LightboxModalMenuProps {
	isGlobal: boolean;
	setIsGlobal: (value: boolean) => void;
	withGlobalToggle: boolean;
	annotationControls?: LightboxAnnotationControls | null;
	onClose: () => void;
}

export function LightboxModalMenu({
	isGlobal,
	setIsGlobal,
	withGlobalToggle,
	annotationControls,
	onClose,
}: LightboxModalMenuProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	const isEditing = annotationControls?.isEditing ?? false;

	/** Render */
	return (
		<div
			className="p-4 flex flex-row justify-between items-center gap-2"
			onClick={(e) => e.stopPropagation()}
		>
			<ButtonGroup isAttached={false}>
				{annotationControls && isEditing && (
					<>
						<Button
							variant="primary"
							icon={<Icon name="X" />}
							onClick={annotationControls.onCancel}
						>
							{t("general.cancel")}
						</Button>
						<Button
							variant="primary"
							icon={<Icon name="Check" />}
							isLoading={annotationControls.isSaving}
							onClick={annotationControls.onSave}
						>
							{t("general.save")}
						</Button>
					</>
				)}
				{annotationControls && !isEditing && (
					<>
						{annotationControls.hasAnnotations && (
							<Button
								variant="primary"
								icon={<Icon name={annotationControls.showAnnotations ? "Eye" : "EyeOff"} />}
								onClick={annotationControls.onToggleShow}
							>
								{t("image_annotator.show_annotations")}
							</Button>
						)}
						{annotationControls.canEdit && (
							<Button
								variant="primary"
								icon={<Icon name="Pencil" />}
								onClick={annotationControls.onEnterEdit}
							>
								{t("image_annotator.edit_annotations")}
							</Button>
						)}
						<Button
							variant="primary"
							icon={<Icon name="Download" />}
							isLoading={annotationControls.isExporting}
							onClick={annotationControls.onDownload}
						>
							{t("image_annotator.download_image")}
						</Button>
						{annotationControls.canShare && (
							<Button
								variant="primary"
								icon={<Icon name="Share2" />}
								isLoading={annotationControls.isExporting}
								onClick={annotationControls.onShare}
							>
								{t("image_annotator.share_image")}
							</Button>
						)}
					</>
				)}
			</ButtonGroup>
			<ButtonGroup isAttached={false}>
				{!isEditing && withGlobalToggle && (
					<Button
						variant="primary"
						onClick={() => setIsGlobal(!isGlobal)}
					>
						{!isGlobal ? t("files.include_all") : t("files.include_container_only")}
					</Button>
				)}
				{!isEditing && (
					<Button
						onClick={onClose}
						variant="primary"
					>
						{t("general.close")}
					</Button>
				)}
			</ButtonGroup>
		</div>
	);
}
